// Design System Constants for MATNO
// A refined, minimal palette with premium feel

// =============================================================================
// COLOR PALETTE
// Neutral base + single accent (teal) for a clean, professional look
// =============================================================================
export const COLORS = {
  // Primary accent - refined teal #428284
  primary: {
    50: '#f0f9f9',
    100: '#d1eeee',
    200: '#a3dcdc',
    300: '#75cbcb',
    400: '#428284', // Main primary - updated
    500: '#366b6d',
    600: '#2a5456',
    700: '#1e3d3f',
    800: '#122627',
    900: '#0a1516',
  },

  // Neutral grays with subtle warm undertone
  gray: {
    50: '#fafafa',
    100: '#f4f4f5',
    200: '#e4e4e7',
    300: '#d4d4d8',
    400: '#a1a1aa',
    500: '#71717a',
    600: '#52525b',
    700: '#3f3f46',
    800: '#27272a',
    900: '#18181b',
    950: '#09090b',
  },
} as const

// Legacy colors for backward compatibility (deprecated)
export const THEME_COLORS = {
  primary: COLORS.primary[400],
  secondary: COLORS.primary[500],
  accent: COLORS.primary[600],
  africa: {
    gold: COLORS.primary[400],
    earth: COLORS.gray[700],
    sunset: COLORS.primary[300],
  },
} as const

// =============================================================================
// TYPOGRAPHY SCALE
// Clean, modern type scale using Inter
// =============================================================================
export const TYPOGRAPHY = {
  fontFamily: {
    sans: ['Inter', 'system-ui', '-apple-system', 'sans-serif'],
    display: ['Inter', 'system-ui', '-apple-system', 'sans-serif'],
  },
  fontSize: {
    xs: ['0.75rem', { lineHeight: '1rem' }],      // 12px
    sm: ['0.875rem', { lineHeight: '1.25rem' }],  // 14px
    base: ['1rem', { lineHeight: '1.5rem' }],     // 16px
    lg: ['1.125rem', { lineHeight: '1.75rem' }],  // 18px
    xl: ['1.25rem', { lineHeight: '1.75rem' }],   // 20px
    '2xl': ['1.5rem', { lineHeight: '2rem' }],    // 24px
    '3xl': ['1.875rem', { lineHeight: '2.25rem' }], // 30px
    '4xl': ['2.25rem', { lineHeight: '2.5rem' }],   // 36px
    '5xl': ['3rem', { lineHeight: '1.1' }],         // 48px
    '6xl': ['3.75rem', { lineHeight: '1.1' }],      // 60px
    '7xl': ['4.5rem', { lineHeight: '1.05' }],      // 72px
  },
  fontWeight: {
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
  },
  letterSpacing: {
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
  },
} as const

// =============================================================================
// SPACING SCALE
// 4px base grid
// =============================================================================
export const SPACING = {
  0: '0',
  px: '1px',
  0.5: '0.125rem',  // 2px
  1: '0.25rem',     // 4px
  1.5: '0.375rem',  // 6px
  2: '0.5rem',      // 8px
  2.5: '0.625rem',  // 10px
  3: '0.75rem',     // 12px
  4: '1rem',        // 16px
  5: '1.25rem',     // 20px
  6: '1.5rem',      // 24px
  8: '2rem',        // 32px
  10: '2.5rem',     // 40px
  12: '3rem',       // 48px
  16: '4rem',       // 64px
  20: '5rem',       // 80px
  24: '6rem',       // 96px
  32: '8rem',       // 128px
} as const

// =============================================================================
// RADII
// Consistent border radii
// =============================================================================
export const RADII = {
  none: '0',
  sm: '0.25rem',    // 4px
  DEFAULT: '0.375rem', // 6px
  md: '0.5rem',     // 8px
  lg: '0.75rem',    // 12px
  xl: '1rem',       // 16px
  '2xl': '1.5rem',  // 24px
  full: '9999px',
} as const

// =============================================================================
// SHADOWS
// Subtle, soft shadows for depth
// =============================================================================
export const SHADOWS = {
  sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  DEFAULT: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
  md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
  lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
  xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
  '2xl': '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  inner: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)',
  glow: '0 0 20px rgb(66 130 132 / 0.15)',
  none: 'none',
} as const

// =============================================================================
// ANIMATION DURATIONS AND EASINGS
// =============================================================================
export const MOTION = {
  duration: {
    fast: '150ms',
    normal: '200ms',
    slow: '300ms',
    slower: '500ms',
  },
  easing: {
    default: 'cubic-bezier(0.4, 0, 0.2, 1)',
    in: 'cubic-bezier(0.4, 0, 1, 1)',
    out: 'cubic-bezier(0, 0, 0.2, 1)',
    inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    spring: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  },
} as const

// =============================================================================
// NAVIGATION
// Anchors ("/#pillars" etc.) navigate to home and scroll to that section.
// =============================================================================
export const NAVIGATION_ITEMS = [
  { path: '/about',          label: 'About' },
  { path: '/#pillars',       label: 'Pillars' },
  { path: '/#what-we-do',    label: 'What We Do' },
  { path: '/research',       label: 'Insights' },
  { path: '/contact#partner', label: 'Partner' },
  { path: '/contact',        label: 'Contact' },
] as const

// =============================================================================
// SITE CONFIG
// =============================================================================
export const SITE_CONFIG = {
  name: 'MATNO',
  motto: 'From Insight to Impact',
  title: 'MATNO — Africa-focused think-and-do tank | From Insight to Impact',
  description:
    'MATNO is an Africa-focused think-and-do tank helping institutions turn data, AI, and digital transformation into inclusive development impact.',
  url: 'https://matno.org',
  ogImage: 'https://matno.org/og-image.jpg',
  keywords: [
    'MATNO',
    'Africa',
    'think tank',
    'do tank',
    'digital transformation',
    'AI for development',
    'digital public infrastructure',
    'policy',
    'governance',
    'human capital',
    'innovation',
  ],
} as const

// =============================================================================
// BREAKPOINTS
// =============================================================================
export const BREAKPOINTS = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
} as const

// =============================================================================
// STRATEGIC PILLARS
// =============================================================================
export const PILLARS = [
  {
    key: 'data-ai',
    title: 'Data & AI for Development',
    description:
      'We help institutions use data and artificial intelligence responsibly to improve decisions, services, and development outcomes.',
    icon: 'network',
    accent: 'blue',
  },
  {
    key: 'dpi',
    title: 'Digital Public Infrastructure & Transformation',
    description:
      'We support the design and implementation of trusted, inclusive, and effective digital systems for governments, institutions, and communities.',
    icon: 'grid',
    accent: 'blue',
  },
  {
    key: 'human-capital',
    title: 'Human Capital, Skills & Future of Work',
    description:
      'We build the skills, leadership, and talent needed for Africa to shape and govern its own digital future.',
    icon: 'users',
    accent: 'green',
  },
  {
    key: 'policy',
    title: 'Policy, Governance & Trust',
    description:
      'We advance governance models, policy frameworks, and safeguards that make digital transformation responsible, secure, inclusive, and trusted.',
    icon: 'shield',
    accent: 'blue',
  },
  {
    key: 'innovation',
    title: 'Innovation, Implementation & Ecosystem Building',
    description:
      'We connect ideas, institutions, and innovators to move from research to pilots, partnerships, and real-world impact.',
    icon: 'route',
    accent: 'green',
  },
] as const

// =============================================================================
// WHAT WE DO
// =============================================================================
export const ACTIVITIES = [
  {
    key: 'research',
    title: 'Research & Analysis',
    description:
      'Policy briefs, research papers, data insights, diagnostics, and practical knowledge products.',
  },
  {
    key: 'advisory',
    title: 'Advisory & Strategy',
    description:
      'Strategies for data, AI, digital transformation, governance, and implementation — tailored to African institutions.',
  },
  {
    key: 'capacity',
    title: 'Capacity Building',
    description:
      'Training, fellowships, workshops, and leadership programs for policymakers, professionals, youth, and institutions.',
  },
  {
    key: 'convening',
    title: 'Convening & Dialogue',
    description:
      'Bringing together governments, development partners, researchers, innovators, civil society, and the private sector around Africa\'s digital transformation agenda.',
  },
  {
    key: 'pilots',
    title: 'Pilots & Implementation Labs',
    description:
      'Practical experimentation, prototypes, pilots, and implementation partnerships to test ideas in real-world contexts.',
  },
] as const

// =============================================================================
// ECOSYSTEM — WHO WE WORK WITH
// =============================================================================
export const ECOSYSTEM_PARTNERS = [
  { key: 'governments',    label: 'Governments & public institutions' },
  { key: 'development',    label: 'Development partners & international organizations' },
  { key: 'universities',   label: 'Universities & research institutions' },
  { key: 'startups',       label: 'Startups & innovation ecosystems' },
  { key: 'private-sector', label: 'Private sector actors' },
  { key: 'civil-society',  label: 'Civil society organizations' },
  { key: 'regional',       label: 'African regional & continental institutions' },
] as const

// =============================================================================
// PARTNER — AREAS OF INTEREST (contact form)
// =============================================================================
export const AREAS_OF_INTEREST = [
  'Research partnership',
  'Funding or grant opportunity',
  'Speaking or event invitation',
  'Advisory or implementation support',
  'Fellowship or talent program',
  'General inquiry',
] as const

// =============================================================================
// VISION & MISSION
// =============================================================================
export const VISION =
  'An Africa where data, artificial intelligence, digital systems, and human capital are used responsibly to build inclusive prosperity, stronger institutions, and better futures for all.'

export const MISSION =
  'MATNO turns research, data, and digital innovation into practical solutions for institutions, communities, and decision-makers across Africa.'

// =============================================================================
// TEAM DATA
// =============================================================================
export const TEAM_MEMBERS = [
  {
    name: 'Research Team',
    role: 'Data Science & AI',
    description: 'Leading cutting-edge research in machine learning and data analytics for African contexts.',
    image: null,
  },
  {
    name: 'Innovation Team',
    role: 'Digital Transformation',
    description: 'Building practical solutions for real-world challenges across the continent.',
    image: null,
  },
  {
    name: 'Policy Team',
    role: 'Strategic Advisory',
    description: 'Bridging research insights with actionable policy recommendations.',
    image: null,
  },
] as const

// =============================================================================
// RESEARCH AREAS
// =============================================================================
export const RESEARCH_AREAS = [
  {
    title: 'Artificial Intelligence',
    description: 'Developing AI solutions tailored to African contexts, from healthcare diagnostics to agricultural optimization.',
    icon: 'brain',
  },
  {
    title: 'Digital Economy',
    description: 'Researching pathways for digital inclusion and economic empowerment across Africa.',
    icon: 'trending-up',
  },
  {
    title: 'Human Capital',
    description: 'Analyzing workforce development strategies to maximize Africa\'s demographic dividend.',
    icon: 'users',
  },
  {
    title: 'Sustainable Development',
    description: 'Exploring technology-driven approaches to environmental and social sustainability.',
    icon: 'leaf',
  },
] as const
